import AppScreenActionsImage from "@/assets/images/app-screens/3.webp";
import AppScreenInviteImage from "@/assets/images/app-screens/4.webp";
import AppScreenTreeImage from "@/assets/images/app-screens/2.webp";
import Logo from "@/components/Logo";
import MobileAppLinks from "@/components/MobileAppLinks";
import { Heading } from "@/components/typography/Heading";
import { Text } from "@/components/typography/Text";
import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks/useUser";
import { logout } from "@/routes";
import { show as showBranch } from "@/routes/branches";
import { create as createLogin, loginDemo } from "@/routes/login";
import { create as createRegistration } from "@/routes/register/info";

import { LogoutIcon, SidebarRight01Icon, TestTubeIcon, WhatsappIcon } from "@hugeicons-pro/core-stroke-standard";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@inertiajs/react";

const appPreviewScreens: { src: string; alt: string; fetchPriority: "high" | "low"; className: string }[] = [
  {
    src: AppScreenActionsImage,
    alt: "واجهة أدوات العائلة في تطبيق أوراق",
    fetchPriority: "low",
    className:
      "absolute bottom-4 start-0 z-10 w-[34%] -rotate-6 opacity-95 sm:w-[32%] md:bottom-12 md:start-2 lg:start-0",
  },
  {
    // The centre screen is the page's largest paint; app.blade.php preloads it.
    src: AppScreenTreeImage,
    alt: "واجهة شجرة العائلة في تطبيق أوراق",
    fetchPriority: "high",
    className: "relative z-20 w-[50%] translate-y-3 sm:w-[48%] md:w-[54%]",
  },
  {
    src: AppScreenInviteImage,
    alt: "واجهة دعوة العائلة في تطبيق أوراق",
    fetchPriority: "low",
    className: "absolute end-0 top-2 z-10 w-[34%] rotate-5 opacity-95 sm:w-[32%] md:end-1 md:top-10 lg:end-0",
  },
];

export default function HeroSplit({ freeNodesCount }: { freeNodesCount: number }) {
  const user = useUser();

  return (
    <section>
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-8 lg:px-12">
        <div className="flex flex-col-reverse items-center gap-12 md:flex-row-reverse md:items-start md:gap-8 lg:gap-16">
          {/* Right Column — Content (60%) */}
          <div className="flex w-full flex-col items-center text-center md:w-[60%] md:items-start md:pt-12 md:text-right">
            <Logo width={80} height={80} className="mb-6 text-green-700" />

            <Heading level={1} size="hero">
              أوراق، منصة متكاملة لشجرة عائلتك.
            </Heading>

            <Text size="lead" className="mt-4 mb-8 max-w-lg">
              أنشئ شجرة عائلتك من الويب، وادع أفراد العائلة لاستكشافها من التطبيق بكل وضوح.
            </Text>

            {/* CTAs */}
            <div className="flex flex-col items-center gap-3 md:items-start">
              {user ? (
                <div className="flex gap-3">
                  <Link href={showBranch()}>
                    <Button variant="secondary" size="lg">
                      <span className="flex items-center gap-2">
                        <HugeiconsIcon icon={SidebarRight01Icon} size={24} color="currentColor" strokeWidth={1.5} />
                        لوحة التحكم
                      </span>
                    </Button>
                  </Link>
                  <Button asChild variant="destructive" size="lg" className="bg-red-500/80 hover:bg-red-600">
                    <Link href={logout()} method="post" as="button">
                      <span className="flex items-center gap-2">
                        <HugeiconsIcon icon={LogoutIcon} size={24} color="currentColor" strokeWidth={1.5} />
                        تسجيل خروج
                      </span>
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
                    <Link href={createRegistration()}>
                      <Button
                        size="lg"
                        className="bg-green-600 px-8 text-base font-semibold text-white hover:bg-green-700"
                      >
                        <span>إنشاء شجرة عائلة</span>
                      </Button>
                    </Link>
                    <Link href={createLogin()}>
                      <Button variant="outline" size="lg" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                        دخول
                      </Button>
                    </Link>
                  </div>

                  <Text as="span" size="meta">
                    سجّل الآن واحصل على رصيد {freeNodesCount} فرد مجانًا
                  </Text>

                  <div className="mt-2 flex flex-col items-center gap-2 md:items-start">
                    <Text as="span" size="meta" className="font-semibold text-gray-600">
                      حمل تطبيق شجرة العائلة
                    </Text>
                    <MobileAppLinks className="justify-center md:justify-start" />
                  </div>

                  <div className="mt-4 flex items-center gap-4">
                    <Button asChild variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
                      <Link href={loginDemo()} method="post" as="button">
                        <span className="flex items-center gap-2">
                          <HugeiconsIcon icon={TestTubeIcon} size={20} color="currentColor" strokeWidth={1.5} />
                          دخول بحساب تجريبي
                        </span>
                      </Link>
                    </Button>

                    <span className="text-gray-300">|</span>

                    <a
                      href="https://wa.me/966550438831"
                      className="flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-green-600"
                    >
                      <HugeiconsIcon icon={WhatsappIcon} size={20} className="text-green-600" />
                      تواصل معنا
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Left Column — Visuals (40%) */}
          <div className="relative flex w-full items-center justify-center md:w-[40%]">
            <div className="relative flex min-h-[430px] w-full max-w-[520px] items-center justify-center sm:min-h-[520px] md:min-h-[640px]">
              <div className="absolute inset-x-8 top-8 bottom-8 rounded-2xl border border-green-100 bg-green-50/70" />
              <div className="absolute inset-x-14 top-16 bottom-12 rounded-xl border border-white bg-white/70" />

              {appPreviewScreens.map((screen) => (
                <div
                  key={screen.alt}
                  className={`${screen.className} aspect-[1290/2796] overflow-hidden rounded-2xl bg-green-700 ring-1 ring-green-950/10`}
                >
                  <img
                    src={screen.src}
                    alt={screen.alt}
                    fetchPriority={screen.fetchPriority}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
